import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Plus, Link2, CalendarCheck, CalendarDays, Globe, CheckCircle2, TrendingUp, Download,
  Eye, Pencil, XCircle, Trash2, ClipboardList, FileText, UserPlus, ListTodo, Mail,
  CalendarPlus, Bell, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import RdvModal from "@/components/RdvModal";
import RdvAvailability from "@/components/rdv/RdvAvailability";
import RdvSettings from "@/components/rdv/RdvSettings";
import RdvTable, { rdvDisplayStatus, StatusBadge, STATUS_DISPLAY, toHHMM, fmtShortDate } from "@/components/rdv/RdvTable";
import { EvolutionChart, SourcesDonut, TransformationWidget, AvailabilityWidget } from "@/components/rdv/RdvCharts";

const TYPE_LABELS = { exterieur: "Présentiel", telephonique: "Téléphone", visio: "Visio" };
const TABS = [
  { id: "overview", label: "Vue d'ensemble" },
  { id: "fixes", label: "RDV fixés" },
  { id: "en_ligne", label: "RDV en ligne" },
  { id: "passes", label: "RDV passés" },
  { id: "dispos", label: "Disponibilités" },
  { id: "parametres", label: "Paramètres" },
];

const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtPeriod = (from, to) => `${fmtShortDate(from)} – ${fmtShortDate(to)} ${to.slice(0, 4)}`;

function periodRange(preset, custom) {
  const now = new Date();
  if (preset === "today") { const t = iso(now); return { from: t, to: t }; }
  if (preset === "week") { const m = mondayOf(now); const s = new Date(m); s.setDate(s.getDate() + 6); return { from: iso(m), to: iso(s) }; }
  if (preset === "prev_month") {
    const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const l = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: iso(f), to: iso(l) };
  }
  if (preset === "year") return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` };
  if (preset === "custom" && custom.from && custom.to) return custom;
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
}

const EvoBadge = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-[11px] text-slate-400">—</span>;
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const cls = value > 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : value < 0 ? "text-rose-700 bg-rose-50 border-rose-200" : "text-slate-500 bg-slate-100 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold border rounded-full px-1.5 py-0.5 ${cls}`}>
      <Icon className="w-3 h-3" />{value > 0 ? "+" : ""}{value} %
    </span>
  );
};

const KPI_DEFS = [
  { key: "total", label: "Total des rendez-vous", Icon: CalendarDays, iconCls: "bg-sky-100 text-sky-700" },
  { key: "en_ligne", label: "RDV en ligne", Icon: Globe, iconCls: "bg-purple-100 text-purple-700" },
  { key: "fixes", label: "RDV fixés", Icon: CalendarCheck, iconCls: "bg-blue-100 text-blue-700" },
  { key: "passes", label: "RDV passés", Icon: CheckCircle2, iconCls: "bg-emerald-100 text-emerald-700" },
  { key: "conversion", label: "Taux de conversion", Icon: TrendingUp, iconCls: "bg-amber-100 text-amber-700", pct: true },
];

export default function Appointments() {
  const [rdvs, setRdvs] = useState([]);
  const [stats, setStats] = useState(null);
  const [avail, setAvail] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [filters, setFilters] = useState({ q: "", type: "all", status: "all", collab: "all", entity: "all" });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const now = new Date();
  const today = iso(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const range = useMemo(() => periodRange(preset, custom), [preset, custom]);

  const load = () => {
    api.get("/appointments").then((r) => setRdvs(r.data || [])).catch(() => {});
    api.get("/appointments/availability/next").then((r) => setAvail(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {});
    api.get("/prospects").then((r) => setProspects(r.data || [])).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/notifications").then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.notifications || [];
      setNotifs(list.filter((n) => (n.type || n.ntype) === "rdv" || (n.url || "").includes("/rdv")).slice(0, 5));
    }).catch(() => {});
    api.post("/notifications/read-type", { type: "rdv" }).then(() => window.dispatchEvent(new Event("sf:notifs"))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/appointments/stats", { params: { from_date: range.from, to_date: range.to } })
      .then((r) => setStats(r.data)).catch(() => setStats(null));
  }, [range.from, range.to]);

  useEffect(() => {
    const f = searchParams.get("focus");
    if (f && rdvs.length && rdvs.some((r) => r.id === f)) navigate(`/rdv/${f}`);
  }, [searchParams, rdvs]);

  const entityName = (r) => r.company || r.prospect_name || r.contact_name || "—";

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return rdvs.filter((r) => {
      if (r.date < range.from || r.date > range.to) return false;
      if (q && ![r.title, r.contact_name, r.company, r.prospect_name, r.email].some((v) => (v || "").toLowerCase().includes(q))) return false;
      if (filters.type !== "all" && r.type !== filters.type) return false;
      if (filters.status !== "all" && rdvDisplayStatus(r, today, nowMin) !== filters.status) return false;
      if (filters.entity !== "all" && r.client_id !== filters.entity && r.prospect_id !== filters.entity) return false;
      return true;
    });
  }, [rdvs, range, filters, today, nowMin]);

  const isPast = (r) => r.date < today || (r.date === today && r.start_min + (r.duration_minutes || 60) <= nowMin);
  const isCancelled = (r) => r.rdv_status === "annule" || r.booking_status === "annule";
  const live = useMemo(() => filtered.filter((r) => !isCancelled(r)), [filtered]);
  const rdvFixes = useMemo(() => live.filter((r) => !r.booked_online && !isPast(r)).sort((a, b) => (a.date + String(a.start_min).padStart(4, "0")).localeCompare(b.date + String(b.start_min).padStart(4, "0"))), [live]);
  const rdvEnLigne = useMemo(() => filtered.filter((r) => r.booked_online).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [filtered]);
  const rdvPasses = useMemo(() => live.filter((r) => isPast(r) || r.rdv_status === "termine").sort((a, b) => b.date.localeCompare(a.date)), [live]);

  const copyLink = async () => {
    try {
      const r = await api.get("/booking/config");
      const d = r.data || {};
      const link = d.link || d.booking_link || (d.token ? `${window.location.origin}/booking/${d.token}` : "");
      if (!link) throw new Error("no link");
      await navigator.clipboard.writeText(link);
      toast.success("Lien de rendez-vous copié.");
    } catch { toast.error("Lien de réservation indisponible"); }
  };

  const cancelRdv = async (r) => {
    try {
      await api.patch(`/appointments/${r.id}`, { rdv_status: "annule" });
      toast.success("Rendez-vous annulé — le créneau est libéré dans l'agenda");
      load();
    } catch { toast.error("Action impossible"); }
  };
  const deleteRdv = async (r) => {
    if (!window.confirm(`Supprimer le rendez-vous « ${r.title} » ?`)) return;
    try { await api.delete(`/appointments/${r.id}`); toast.success("Rendez-vous supprimé"); load(); }
    catch { toast.error("Suppression impossible"); }
  };
  const createTask = async (r) => {
    try {
      await api.post("/tasks", {
        title: `Après RDV — ${entityName(r)} : ${r.title}`.slice(0, 160),
        client_id: r.client_id || null, deadline: r.date, service: "Commercial",
        source_type: "appointment", source_id: r.id,
        source_label: `Rendez-vous — ${r.title}`, source_url: "/rdv",
      });
      toast.success("Tâche ajoutée à la To-do");
      window.dispatchEvent(new Event("sf:tasks"));
    } catch { toast.error("Création impossible"); }
  };

  const exportCsv = () => {
    const rows = [["Date", "Heure", "Client / Prospect", "Contact", "Type", "Statut", "Source", "Email", "Téléphone", "Objet"]];
    filtered.forEach((r) => rows.push([
      r.date, toHHMM(r.start_min), entityName(r), r.contact_name || "", TYPE_LABELS[r.type] || r.type,
      STATUS_DISPLAY[rdvDisplayStatus(r, today, nowMin)]?.label || "", r.source || (r.booked_online ? "lien_reservation" : "agenda_interne"),
      r.email || "", r.phone || "", (r.title || "").replace(/[\r\n]+/g, " "),
    ]));
    const csv = "﻿" + rows.map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = `rendez-vous_${range.from}_${range.to}.csv`;
    a.click();
    toast.success("Export CSV téléchargé");
  };

  const entityCol = { key: "entity", label: "Client / Prospect", render: (r) => (
    <div className="min-w-0"><p className="font-medium text-slate-900 truncate max-w-[160px]">{entityName(r)}</p>
      {r.contact_name && r.contact_name !== entityName(r) && <p className="text-[11px] text-slate-400 truncate">{r.contact_name}</p>}</div>
  ) };
  const dateCol = { key: "date", label: "Date", render: (r) => <span className="whitespace-nowrap capitalize">{fmtShortDate(r.date)}</span> };
  const timeCol = { key: "heure", label: "Heure", render: (r) => <span className="whitespace-nowrap">{toHHMM(r.start_min)}</span> };
  const typeCol = { key: "type", label: "Type", render: (r) => <span className="text-xs bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 whitespace-nowrap">{TYPE_LABELS[r.type] || r.type_label}</span> };
  const collabCol = { key: "collab", label: "Collaborateur", render: () => <span className="text-xs text-slate-600 whitespace-nowrap">{me?.name || "—"}</span> };
  const statusCol = { key: "statut", label: "Statut", render: (r) => <StatusBadge status={rdvDisplayStatus(r, today, nowMin)} /> };
  const objetCol = { key: "objet", label: "Objet", render: (r) => <span className="text-xs text-slate-500 line-clamp-2 max-w-[200px]">{r.title}</span> };
  const crCol = { key: "cr", label: "Compte rendu", render: (r) => {
    const done = (r.report || "").trim() || ["finalise", "envoye"].includes(r.report_status);
    return done
      ? <span className="text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">Complété</span>
      : <span className="text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">À faire</span>;
  } };

  const fixesActions = (r) => [
    { label: "Voir le rendez-vous", icon: <Eye className="w-4 h-4 mr-2" />, onClick: () => navigate(`/rdv/${r.id}`) },
    { label: "Modifier / reporter", icon: <Pencil className="w-4 h-4 mr-2" />, onClick: () => setEditing(r) },
    { label: "Annuler", icon: <XCircle className="w-4 h-4 mr-2" />, onClick: () => cancelRdv(r) },
    { label: "Supprimer", icon: <Trash2 className="w-4 h-4 mr-2" />, danger: true, onClick: () => deleteRdv(r) },
  ];
  const enLigneActions = (r) => [
    { label: "Voir le rendez-vous", icon: <Eye className="w-4 h-4 mr-2" />, onClick: () => navigate(`/rdv/${r.id}`) },
    { label: "Copier le lien de RDV", icon: <Link2 className="w-4 h-4 mr-2" />, onClick: copyLink },
    { label: "Annuler", icon: <XCircle className="w-4 h-4 mr-2" />, onClick: () => cancelRdv(r) },
  ];
  const passesActions = (r) => [
    { label: "Consulter le compte rendu", icon: <ClipboardList className="w-4 h-4 mr-2" />, onClick: () => navigate(`/rdv/${r.id}`) },
    { label: "Créer une tâche", icon: <ListTodo className="w-4 h-4 mr-2" />, onClick: () => createTask(r) },
    { label: "Créer un devis", icon: <FileText className="w-4 h-4 mr-2" />, onClick: () => navigate("/comptabilite?tab=devis") },
  ];

  const pendingReports = rdvPasses.filter((r) => !((r.report || "").trim() || ["finalise", "envoye"].includes(r.report_status)));
  const quickActions = [
    { key: "cr", label: "Faire le compte rendu", Icon: ClipboardList, onClick: () => pendingReports[0] && navigate(`/rdv/${pendingReports[0].id}`), disabled: !pendingReports.length },
    { key: "devis", label: "Créer un devis", Icon: FileText, onClick: () => navigate("/comptabilite?tab=devis") },
    { key: "client", label: "Convertir en client", Icon: UserPlus, onClick: () => navigate("/prospection") },
    { key: "taches", label: "Créer des tâches", Icon: ListTodo, onClick: () => navigate("/taches") },
    { key: "email", label: "Envoyer un email de suivi", Icon: Mail, onClick: () => rdvPasses[0] && navigate(`/rdv/${rdvPasses[0].id}`), disabled: !rdvPasses.length },
    { key: "rdv", label: "Planifier un nouveau RDV", Icon: CalendarPlus, onClick: () => setCreating(true) },
  ];

  const selectCls = "h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200";

  return (
    <div className="space-y-5" data-testid="rdv-page">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Rendez-vous</h1>
          <p className="text-sm text-slate-500 mt-1">Gérez tous vos rendez-vous, suivez les demandes en ligne et transformez vos échanges en opportunités.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-xl gap-1.5 text-xs" data-testid="rdv-period-select">
                <CalendarDays className="w-4 h-4" />{fmtPeriod(range.from, range.to)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {[["today", "Aujourd'hui"], ["week", "Cette semaine"], ["month", "Ce mois"], ["prev_month", "Mois précédent"], ["year", "Année"], ["custom", "Période personnalisée"]].map(([id, label]) => (
                <DropdownMenuItem key={id} onClick={() => setPreset(id)} data-testid={`rdv-period-${id}`}
                  className={preset === id ? "font-semibold" : ""}>{label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="rounded-xl gap-1.5 text-xs" onClick={copyLink} data-testid="rdv-copy-link">
            <Link2 className="w-4 h-4" />Copier le lien de RDV
          </Button>
          <Button className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 text-xs" onClick={() => setCreating(true)} data-testid="rdv-new">
            <Plus className="w-4 h-4" />Nouveau rendez-vous
          </Button>
        </div>
      </div>
      {preset === "custom" && (
        <div className="flex items-center gap-2" data-testid="rdv-period-custom">
          <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="h-9 w-40 rounded-lg text-xs" data-testid="rdv-period-from" />
          <span className="text-xs text-slate-400">→</span>
          <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="h-9 w-40 rounded-lg text-xs" data-testid="rdv-period-to" />
        </div>
      )}

      {/* Sous-navigation */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="rdv-subnav">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`rdv-tab-${t.id}`}
            className={`px-3.5 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="rdv-kpis">
        {KPI_DEFS.map(({ key, label, Icon, iconCls, pct }) => (
          <div key={key} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`rdv-kpi-${key}`}>
            <div className="flex items-center justify-between">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconCls}`}><Icon className="w-4 h-4" /></span>
              <EvoBadge value={stats?.kpis?.[key]?.evolution} />
            </div>
            <p className="text-2xl font-bold tracking-tight text-slate-900 mt-2.5" data-testid={`rdv-kpi-${key}-value`}>
              {stats ? (pct ? `${stats.kpis[key].value} %` : stats.kpis[key].value) : "—"}
            </p>
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2" data-testid="rdv-filters">
        <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          placeholder="Rechercher un rendez-vous…" className="h-9 w-64 rounded-lg text-xs" data-testid="rdv-filter-search" />
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className={selectCls} data-testid="rdv-filter-type">
          <option value="all">Tous les types</option>
          <option value="visio">Visio</option><option value="telephonique">Téléphone</option><option value="exterieur">Présentiel</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={selectCls} data-testid="rdv-filter-status">
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_DISPLAY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.collab} onChange={(e) => setFilters({ ...filters, collab: e.target.value })} className={selectCls} data-testid="rdv-filter-collab">
          <option value="all">Tous les collaborateurs</option>
          {me && <option value={me.id}>{me.name}</option>}
        </select>
        <select value={filters.entity} onChange={(e) => setFilters({ ...filters, entity: e.target.value })} className={selectCls} data-testid="rdv-filter-entity">
          <option value="all">Tous les clients / prospects</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          {prospects.map((p) => <option key={p.id} value={p.id}>{p.name} (prospect)</option>)}
        </select>
        <Button variant="outline" className="rounded-xl gap-1.5 text-xs ml-auto" onClick={exportCsv} data-testid="rdv-export">
          <Download className="w-4 h-4" />Exporter
        </Button>
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
          <div className="space-y-5">
            <EvolutionChart series={stats?.series || []} />
            <RdvTable title="RDV fixés" subtitle="Rendez-vous ajoutés directement dans l'Agenda" count={rdvFixes.length}
              testid="rdv-table-fixes" columns={[dateCol, timeCol, entityCol, typeCol, collabCol, statusCol]} rows={rdvFixes.slice(0, 8)}
              onRowClick={(r) => navigate(`/rdv/${r.id}`)} actions={fixesActions} emptyText="Aucun RDV fixé sur cette période."
              headerAction={<button onClick={() => setCreating(true)} data-testid="rdv-add-fixed"
                className="text-xs font-semibold text-[#0369A1] hover:underline whitespace-nowrap">+ Ajouter un RDV</button>} />
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="rdv-notifs">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Bell className="w-4 h-4 text-slate-400" />Notifications récentes</h3>
                <Link to="/notifications" className="text-xs font-semibold text-[#0369A1] hover:underline" data-testid="rdv-notifs-all">Voir toutes ›</Link>
              </div>
              {notifs.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center" data-testid="rdv-notifs-empty">Aucune notification récente.</p>
              ) : (
                <ul className="space-y-2.5">
                  {notifs.map((n) => (
                    <li key={n.id} className="flex items-start gap-2.5" data-testid={`rdv-notif-${n.id}`}>
                      <span className="w-7 h-7 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center shrink-0 mt-0.5"><Bell className="w-3.5 h-3.5" /></span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate">{n.title}</p>
                        <p className="text-[11px] text-slate-500 line-clamp-2">{n.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="space-y-5">
            <SourcesDonut sources={stats?.sources || []} />
            <RdvTable title="RDV en ligne" subtitle="Demandes via le lien de réservation" count={rdvEnLigne.length}
              testid="rdv-table-en-ligne" columns={[dateCol, timeCol, entityCol, objetCol, statusCol]} rows={rdvEnLigne.slice(0, 8)}
              onRowClick={(r) => navigate(`/rdv/${r.id}`)} actions={enLigneActions} emptyText="Aucune réservation en ligne sur cette période."
              headerAction={<button onClick={copyLink} data-testid="rdv-see-link"
                className="text-xs font-semibold text-[#0369A1] hover:underline whitespace-nowrap">Voir le lien</button>} />
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="rdv-quick-actions">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Actions rapides après un rendez-vous</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {quickActions.map(({ key, label, Icon, onClick, disabled }) => (
                  <button key={key} onClick={onClick} disabled={disabled} data-testid={`rdv-quick-${key}`}
                    className="flex items-center gap-2.5 text-left text-xs font-medium text-slate-700 border border-slate-200 rounded-xl px-3 py-2.5 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                    <span className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5 text-slate-600" /></span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-5">
            <AvailabilityWidget avail={avail} />
            <RdvTable title="RDV passés" subtitle="Rendez-vous réalisés avec compte rendu et suivi" count={rdvPasses.length}
              testid="rdv-table-passes" columns={[dateCol, entityCol, typeCol, crCol]} rows={rdvPasses.slice(0, 8)}
              onRowClick={(r) => navigate(`/rdv/${r.id}`)} actions={passesActions} emptyText="Aucun RDV passé sur cette période." />
            <TransformationWidget transformation={stats?.transformation} />
          </div>
        </div>
      )}

      {tab === "fixes" && (
        <RdvTable title="RDV fixés" subtitle="Rendez-vous ajoutés directement dans l'Agenda" count={rdvFixes.length}
          testid="rdv-table-fixes-full" columns={[dateCol, timeCol, entityCol, typeCol, collabCol, statusCol]} rows={rdvFixes}
          onRowClick={(r) => navigate(`/rdv/${r.id}`)} actions={fixesActions} emptyText="Aucun RDV fixé sur cette période."
          headerAction={<button onClick={() => setCreating(true)} data-testid="rdv-add-fixed-full"
            className="text-xs font-semibold text-[#0369A1] hover:underline">+ Ajouter un RDV</button>} />
      )}
      {tab === "en_ligne" && (
        <RdvTable title="RDV en ligne" subtitle="Demandes via le lien de réservation" count={rdvEnLigne.length}
          testid="rdv-table-en-ligne-full" columns={[dateCol, timeCol, entityCol, objetCol, statusCol]} rows={rdvEnLigne}
          onRowClick={(r) => navigate(`/rdv/${r.id}`)} actions={enLigneActions} emptyText="Aucune réservation en ligne sur cette période."
          headerAction={<button onClick={copyLink} data-testid="rdv-see-link-full"
            className="text-xs font-semibold text-[#0369A1] hover:underline">Voir le lien</button>} />
      )}
      {tab === "passes" && (
        <RdvTable title="RDV passés" subtitle="Rendez-vous réalisés avec compte rendu et suivi" count={rdvPasses.length}
          testid="rdv-table-passes-full" columns={[dateCol, entityCol, typeCol, crCol, statusCol]} rows={rdvPasses}
          onRowClick={(r) => navigate(`/rdv/${r.id}`)} actions={passesActions} emptyText="Aucun RDV passé sur cette période." />
      )}
      {tab === "dispos" && <RdvAvailability />}
      {tab === "parametres" && <RdvSettings />}

      {creating && <RdvModal rdv={null} onClose={() => setCreating(false)} onSaved={load} />}
      {editing && <RdvModal rdv={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
